/**
 * Clone Lottie JSON and replace pure-black stroke/fill colors with custom RGBA (0–1).
 * Bodymovin stores color as `c: { a: 0, k: [r,g,b,a] }` (static) or `a: 1` with keyframed `k`.
 */
export type LottieRgba = [number, number, number, number];

/** `#RRGGBB` / `#RGB` → Lottie RGBA components (0–1). */
export function hexToLottieRgba(hex: string): LottieRgba {
  const raw = hex.trim().replace(/^#/, "");
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;
  if (full.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(full)) {
    return [0, 0, 0, 1];
  }
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  return [r, g, b, 1];
}

function isPureBlackChannel(c: number[]): boolean {
  if (c.length < 3) return false;
  const [r, g, b] = c;
  const a = c[3] ?? 1;
  return r === 0 && g === 0 && b === 0 && (a === 1 || a === undefined);
}

/** Normalize Lottie color prop to a mutable RGBA number array, or null if not a simple static black target. */
function replaceBlackInColorProp(c: unknown, replacement: LottieRgba): void {
  if (c === null || typeof c !== "object") return;

  // Bare array (rare in exports but supported)
  if (Array.isArray(c) && typeof c[0] === "number") {
    if (isPureBlackChannel(c)) {
      c[0] = replacement[0];
      c[1] = replacement[1];
      c[2] = replacement[2];
      c[3] = replacement[3];
    }
    return;
  }

  const prop = c as Record<string, unknown>;
  const animated = prop.a === 1;

  if (!animated) {
    const k = prop.k;
    if (Array.isArray(k) && typeof k[0] === "number" && k.length >= 3) {
      if (isPureBlackChannel(k as number[])) {
        prop.k = [...replacement];
      }
    }
    return;
  }

  // Keyframed color: each entry may hold `s: [r,g,b,a]`
  const frames = prop.k;
  if (!Array.isArray(frames)) return;
  for (const frame of frames) {
    if (frame === null || typeof frame !== "object") continue;
    const s = (frame as Record<string, unknown>).s;
    if (Array.isArray(s) && typeof s[0] === "number" && isPureBlackChannel(s as number[])) {
      const arr = s as number[];
      arr[0] = replacement[0];
      arr[1] = replacement[1];
      arr[2] = replacement[2];
      arr[3] = replacement[3];
    }
  }
}

function walk(node: unknown, stroke: LottieRgba, fill: LottieRgba): void {
  if (node === null || typeof node !== "object") return;
  if (Array.isArray(node)) {
    for (const item of node) walk(item, stroke, fill);
    return;
  }
  const o = node as Record<string, unknown>;
  if (o.ty === "st" && o.c !== undefined) {
    replaceBlackInColorProp(o.c, stroke);
  } else if (o.ty === "fl" && o.c !== undefined) {
    replaceBlackInColorProp(o.c, fill);
  }
  for (const v of Object.values(o)) {
    walk(v, stroke, fill);
  }
}

export function recolorLottieBlack(
  animationData: object,
  stroke: LottieRgba,
  fill: LottieRgba,
): object {
  const clone = JSON.parse(JSON.stringify(animationData)) as Record<
    string,
    unknown
  >;
  walk(clone, stroke, fill);
  return clone;
}
